import { useRef, useState } from 'react';

import { IMinimalCartEntry } from 'features/cart/types';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isCombo } from 'state/unified-menu/utils';

import { SlotsSelections } from './types';
import {
  initializeSlotSelections,
  initializeSlotSelectionsFromLegacyCartEntry,
  initializeSlotSelectionsFromMinimalCartEntry,
} from './utils';

type UseSlotsSelectionsParams = {
  item: ILocalizedMenuEntry;
  selectedProduct: ILocalizedMenuEntry;
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  editingCartEntry?: ILegacyCartEntry | IMinimalCartEntry;
  posServiceMode: PosServiceMode;
  isMinimalCartUIEnabled: boolean;
  activeDayParts: IDayPartBoundary[];
  storeHasBurgersForBreakfast: boolean;
};

function initSlotSelections({
  item,
  selectedProduct,
  indexedMenu,
  editingCartEntry,
  posServiceMode,
  isMinimalCartUIEnabled,
  activeDayParts,
  storeHasBurgersForBreakfast,
}: UseSlotsSelectionsParams): SlotsSelections {
  if (!editingCartEntry) {
    return initializeSlotSelections(
      selectedProduct,
      indexedMenu,
      isCombo(item),
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast
    );
  }
  if (isMinimalCartUIEnabled) {
    return initializeSlotSelectionsFromMinimalCartEntry(
      selectedProduct,
      indexedMenu,
      editingCartEntry as IMinimalCartEntry
    );
  }
  return initializeSlotSelectionsFromLegacyCartEntry(
    selectedProduct,
    indexedMenu,
    editingCartEntry as ILegacyCartEntry
  );
}

export function useSlotsSelections(params: UseSlotsSelectionsParams) {
  // Stores a snapshot of the slots selections when the user goes to "edit"
  // this will serve as the undo state when the user cancels the edit
  const slotsSelectionsSnapshot = useRef<SlotsSelections | undefined>(undefined);
  const [slotsSelections, setSlotsSelections] = useState<SlotsSelections>(() =>
    initSlotSelections(params)
  );

  return { slotsSelections, setSlotsSelections, slotsSelectionsSnapshot } as const;
}
